import React from "react";
import { motion, AnimatePresence } from "framer-motion";

interface ConfirmCloseModalProps {
  show: boolean;
  isDarkMode: boolean;
  vehicleNumber: string;
  isUpdating: boolean;
  comment: string;
  onCommentChange: (comment: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}

export const ConfirmCloseModal: React.FC<ConfirmCloseModalProps> = ({
  show,
  isDarkMode,
  vehicleNumber,
  isUpdating,
  comment,
  onCommentChange,
  onClose,
  onConfirm,
}) => {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-lg mx-4 rounded-xl shadow-2xl overflow-hidden ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Close Inspection
                  </h3>
                  <p className="text-red-100 text-sm">Are you sure?</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <div
                className={`p-4 rounded-lg ${
                  isDarkMode ? "bg-gray-700/50" : "bg-gray-100"
                }`}
              >
                <p
                  className={`text-base leading-relaxed mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Vehicle:{" "}
                  <strong className="text-phoenix-600">#{vehicleNumber}</strong>
                </p>
                <p
                  className={`text-base leading-relaxed ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  This will mark the inspection as <strong>closed</strong> and
                  it will be removed from the active board.
                </p>
              </div>

              {/* Comment Field */}
              <div>
                <label
                  htmlFor="close-comment"
                  className={`block text-sm font-semibold mb-2 ${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Closing Comment{" "}
                  <span className="text-gray-500 font-normal">(Optional)</span>
                </label>
                <textarea
                  id="close-comment"
                  value={comment}
                  onChange={(e) => onCommentChange(e.target.value)}
                  placeholder="Add any notes or reasons for closing this inspection..."
                  rows={4}
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-colors resize-none focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500 ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
                <p
                  className={`text-xs mt-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {comment.length} characters
                </p>
              </div>
            </div>

            {/* Footer */}
            <div
              className={`px-6 py-4 flex justify-end gap-3 ${
                isDarkMode ? "bg-gray-900/50" : "bg-gray-50"
              }`}
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className={`px-6 py-2.5 rounded-lg font-semibold transition-colors ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                }`}
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onConfirm}
                disabled={isUpdating}
                className="px-6 py-2.5 rounded-lg font-semibold bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUpdating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Closing...
                  </>
                ) : (
                  "Close Inspection"
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
